import React, { useContext, useCallback, useState } from 'react';
import styled from 'styled-components';

import AddTask from 'actions/composition/add_task';
import Assignee from 'models/assignee';
import { AttachmentStyles, RichTextEditorStyles, EmojiStyles } from './shared/editor_styles';
import Composer, { Header, HeaderText, useComposer } from './shared/composer';
import ComposerContext from './contexts/composer_context';
import DatetimePickerMenu from '../lib/datetime_picker_menu';
import { EditorContainer, useEditor } from './shared/editor';
import EditTask from 'actions/composer/edit_task';
import { getDatetimeFromTimestamp, getTimestampFromDatetime } from 'components/lib/date/datetime_helpers';
import RichTextEditor from './editors/rich_text';
import SearchableTaskAssignmentMenu from 'components/lib/assignment_menu/searchable_task_assignment_menu';
import UpdateComposition from 'actions/composer/update_composition';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export const StyledRichEditorContainer = styled.div`
  display: flex;
  overflow: hidden;
`;

const StyledRichEditorElement = styled.div`
  margin-left: auto;
  width: 100%;
`;

const LeftControls = styled.div`
  display: flex;
`;

export function TaskComposer({ isEditing }) {
  const composer = useComposer(isEditing ? EditTask : AddTask);
  const editorProps = useEditor('bodyHtml', composer);
  const [composerProps] = composer;

  return (
    <Composer includeAttachments submitText={`${isEditing ? 'Save ' : 'Add '}Task`} {...composerProps}>
      <Header>
        <LeftControls>
          <HeaderText color="green400">{`${isEditing ? 'Edit ' : 'Create '}Task`}</HeaderText>
          <DatePicker />
        </LeftControls>
        <AssigneeSelector />
      </Header>
      <StyledRichEditorContainer>
        <StyledRichEditorElement>
          <RichTextEditorStyles {...editorProps}>
            <EmojiStyles {...editorProps} />
            <AttachmentStyles alwaysShow />
          </RichTextEditorStyles>
        </StyledRichEditorElement>
      </StyledRichEditorContainer>
      <EditorContainer includeAttachments>
        <RichTextEditor placeholder="Type to enter task description" {...editorProps} />
      </EditorContainer>
    </Composer>
  );
}

export default React.memo(TaskComposer);

function AssigneeSelector() {
  const { composition } = useContext(ComposerContext);
  const executeAction = useExecuteAction();
  const assignee = composition.content.assignee;
  const compositionId = composition.id;
  const onChangeAssignee = useCallback(
    attrs => {
      if (attrs.routingGroupId) {
        const assignee = new Assignee({
          agentId: attrs.agentId,
          routingGroupId: attrs.routingGroupId,
        });
        executeAction(UpdateComposition, {
          compositionId,
          attrs: {
            assignee,
          },
        });
      }
    },
    [compositionId, executeAction]
  );
  return (
    <SearchableTaskAssignmentMenu
      agentId={assignee && assignee.agentId}
      routingGroupId={assignee && assignee.routingGroupId}
      onSelect={onChangeAssignee}
      label="Assign to"
    />
  );
}

const StyledDatePickerContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledDatePickerDue = styled.div`
  align-items: center;
  color: #999999;
  display: flex;
  font-size: 12px;
  padding-right: 10px;
  line-height: 12px;
`;

const StyledDatePicker = styled.div`
  flex-grow: 1;
  align-items: center;
  display: flex;
  flex-grow: 1;
`;

export function DatePicker() {
  const { composition } = useContext(ComposerContext);
  const executeAction = useExecuteAction();
  const compositionId = composition.id;
  const [timestamp, setTimestamp] = useState(getDatetimeFromTimestamp(composition.content.dueAt));
  const onChangeDatetime = useCallback(
    attrs => {
      if (attrs.date) {
        let ts = getTimestampFromDatetime(attrs);
        setTimestamp(attrs);
        executeAction(UpdateComposition, {
          compositionId,
          attrs: {
            dueAt: ts,
          },
        });
      }
    },
    [compositionId, executeAction]
  );
  return (
    <StyledDatePickerContainer>
      <StyledDatePickerDue>Due</StyledDatePickerDue>
      <StyledDatePicker>
        <DatetimePickerMenu
          className="taskCompositionForm-timePickerMenu"
          onChange={onChangeDatetime}
          placeholderText="Due date"
          value={timestamp}
        />
      </StyledDatePicker>
    </StyledDatePickerContainer>
  );
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/composer/task_composer.jsx